/**
 * Attribute allocation and respec rules (#97). A character's attributes start
 * at `ATTRIBUTE_MIN` and are raised with earned points: the creation points,
 * plus one per Veil Attunement milestone reached. A respec resets every
 * earned point and reallocates them all, paid with banked essence; each one
 * costs more than the last. Amounts are essence exchange value, first-pass
 * numbers to tune.
 */
import { countAttributePointsSpent } from './attribute-points';
import { ATTRIBUTE_MAX, ATTRIBUTE_MIN, CREATION_ATTRIBUTE_POINTS } from './constants';
import { resolveAttunementAttributePoints } from './essence-progression';
import type { CharacterAttributes } from './types/game';

/** Price of a character's first respec; it doubles with each respec after. */
export const RESPEC_BASE_COST_VALUE = 100;

/** Every character attribute, in display order. */
export const ATTRIBUTE_KEYS: readonly (keyof CharacterAttributes)[] = [
    'vitality',
    'strength',
    'agility',
    'quickness',
    'intellect',
    'endurance',
];

/** What a character's next respec costs, given how many they've already had: 100, 200, 400, ... */
export function resolveRespecCost(respecCount: number): number {
    return RESPEC_BASE_COST_VALUE * 2 ** Math.max(0, respecCount);
}

/**
 * Attribute points a character has earned: the creation points plus those
 * each Veil Attunement milestone grants, reached at `veilAttunementPercent` (0-100).
 */
export function resolveEarnedAttributePoints(veilAttunementPercent = 0): number {
    return CREATION_ATTRIBUTE_POINTS + resolveAttunementAttributePoints(veilAttunementPercent);
}

/** Whether two allocations put every attribute at the same value: a respec to it changes nothing. */
export function isSameAttributeAllocation(a: CharacterAttributes, b: CharacterAttributes): boolean {
    return ATTRIBUTE_KEYS.every((key) => a[key] === b[key]);
}

/**
 * Why an attribute allocation is invalid, or undefined if it's valid: every
 * attribute a whole number within the caps, and exactly `earnedPoints` spent
 * above the minimum. Used for character creation and for respecs.
 */
export function findAttributeAllocationError(attributes: CharacterAttributes, earnedPoints: number): string | undefined {
    for (const key of ATTRIBUTE_KEYS) {
        const value = attributes[key];
        if (!Number.isInteger(value) || value < ATTRIBUTE_MIN || value > ATTRIBUTE_MAX) {
            return `${key} must be a whole number from ${ATTRIBUTE_MIN} to ${ATTRIBUTE_MAX}.`;
        }
    }
    const spent = countAttributePointsSpent(attributes);
    if (spent !== earnedPoints) {
        return `Spend exactly ${earnedPoints} attribute points (${spent} spent).`;
    }
    return undefined;
}
